/**
 * Shared parsing helpers for identity DTOs.
 * Jackson lists/sets become arrays; missing optional collections become [].
 */

export function requireObject(raw, typeName) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`${typeName}: expected an object`);
  }
  return raw;
}

export function requireString(raw, field, typeName) {
  const value = raw[field];
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`${typeName}: ${field} is required`);
  }
  return value;
}

export function asStringArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item) => typeof item === 'string');
}

export function asStringMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const result = {};
  Object.entries(value).forEach(([key, item]) => {
    if (typeof item === 'string') {
      result[key] = item;
    }
  });
  return result;
}

export function mapDtoList(raw, typeName, mapper) {
  if (raw == null) {
    return [];
  }
  if (!Array.isArray(raw)) {
    throw new Error(`${typeName}[]: expected an array`);
  }
  return raw.map(mapper);
}

/** Jackson Instant is epoch millis or ISO-8601. Missing values stay null. */
export function asInstant(value) {
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }
  return null;
}

export function requireInstant(raw, field, typeName) {
  const value = asInstant(raw[field]);
  if (value == null) {
    throw new Error(`${typeName}: ${field} is required`);
  }
  return value;
}

export function requireNumber(raw, field, typeName) {
  const value = raw[field];
  if (typeof value !== 'number') {
    throw new Error(`${typeName}: ${field} is required`);
  }
  return value;
}

export function optionalNumber(value) {
  return typeof value === 'number' ? value : null;
}

export function asBoolean(value) {
  return value === true;
}

export function optionalString(value) {
  return typeof value === 'string' ? value : null;
}

/** Null stays null. Any other value must be one of `allowed`. */
export function optionalEnum(value, allowed, typeName, field) {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string' && Object.values(allowed).includes(value)) {
    return value;
  }
  throw new Error(`${typeName}: unknown ${field} "${value}"`);
}

/** Must be one of `allowed`; null is rejected too. */
export function requireEnum(value, allowed, typeName, field) {
  if (value == null) {
    throw new Error(`${typeName}: ${field} is required`);
  }
  return optionalEnum(value, allowed, typeName, field);
}

export function mapStringList(raw, typeName) {
  return mapDtoList(raw, typeName, (item) => {
    if (typeof item !== 'string') {
      throw new Error(`${typeName}: expected a string`);
    }
    return item;
  });
}

/**
 * Copies `field` only when Jackson wrote it. On `NON_NULL` types an absent field is not the same
 * as an empty one: the document tiles read `undefined` as "not generated yet".
 */
export function assignIfPresent(target, source, field, mapper) {
  if (source[field] !== undefined) {
    target[field] = mapper(source[field]);
  }
}

export function mapObjectList(value, mapper) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(mapper);
}
